import Icon from '@atoms/Icon/Icon';
import Image from '@atoms/Image/Image';
import IExternalVideo from '@models/IExternalVideo';
import ExternalVideo from '@molecules/ExternalVideo/ExternalVideo';
import VideoModal from '@molecules/VideoModal/VideoModal';
import useCookieStatusStore from '@state/CookieStatusStore';
import useAriaLabels from '@utils/hooks/useAriaLabels';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useState } from 'react';

interface Props extends IExternalVideo {
  readonly anchorId?: string;
  readonly embedded?: boolean;
  readonly dataTestId?: string;
}

const EmbeddedVideo = ({
  videoProvider,
  videoId,
  title,
  subTitle,
  anchorId = '',
  meta,
  coverImage,
  embedded = false,
  dataTestId = 'CT-10-A-Video-Embed',
}: Props) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const { cookieStatus } = useCookieStatusStore((state) => ({
    cookieStatus: state.cookieStatus,
  }));
  const { playVideoAriaLabel } = useAriaLabels();

  const onVideoModalClose = useCallback(() => {
    if (cookieStatus) {
      setIsPlaying((oldState) => !oldState);
    }
  }, [cookieStatus]);

  return embedded ? (
    <section
      className="w-full bg-white text-black-100 dark:bg-black-100 dark:text-white"
      id={`a-${anchorId}`}
      data-testid={dataTestId}
    >
      <div className="relative w-full">
        <div className="aspect-video">
          {cookieStatus && (
            <ExternalVideo
              videoProvider={videoProvider}
              videoId={videoId}
              autoPlay={isPlaying}
              isInvisible={!isPlaying}
              onEndedVideo={() => setIsPlaying(false)}
              title={title}
            />
          )}
        </div>
        <AnimatePresence>
          {!isPlaying && (
            <motion.div
              initial={{ opacity: 1 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute -bottom-[0.1rem] left-0 top-0 flex aspect-video w-full cursor-pointer items-center justify-center"
            >
              {coverImage && (
                <Image
                  classes="z-10 w-full"
                  epiEdit={meta?.editHints?.coverImage as string | undefined}
                  image={coverImage}
                  imageClasses="w-full aspect-video"
                  data-testid={`${dataTestId}-Image`}
                />
              )}
              <button
                type="button"
                aria-label={playVideoAriaLabel}
                onClick={() => cookieStatus && setIsPlaying(true)}
                className="absolute bottom-2.5 left-2.5 z-10"
                data-testid={`${dataTestId}-Play-Button`}
              >
                <div className="h-[2.1875rem] w-[2.1875rem] md:h-[2.875rem] md:w-[2.875rem]">
                  <Icon name="play_icon_circle" size="100%" />
                </div>
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  ) : (
    <section
      className="two-col-spacing component-padding bg-white text-black-100 dark:bg-black-100 dark:text-white"
      id={`a-${anchorId}`}
      data-testid={dataTestId}
    >
      <div className="layout">
        <div className="inner grid gap-y-7.5 xl:grid-cols-12 xl:gap-y-[70px]">
          {coverImage && (
            <div
              className={classNames(
                'relative flex items-center justify-center xl:col-span-full',
                !isVideoModalOpen && coverImage ? 'visible' : 'invisible'
              )}
              data-testid={`${dataTestId}-Cover-Image`}
            >
              <Image
                classes="w-full object-cover"
                imageClasses="w-full"
                image={coverImage}
                epiEdit={meta?.editHints?.coverImage as string | undefined}
                data-testid={`${dataTestId}-Image`}
              />

              <button
                type="button"
                onClick={() => {
                  if (cookieStatus)
                    setIsVideoModalOpen((oldState) => !oldState);
                }}
                className="absolute bottom-2.5 left-2.5 z-10 md:bottom-5 md:left-5"
                aria-label={playVideoAriaLabel}
                data-testid={`${dataTestId}-Play-Button`}
              >
                <div className="h-[2.1875rem] w-[2.1875rem] md:h-[2.875rem] md:w-[2.875rem]">
                  <Icon name="play_icon_circle" size="100%" />
                </div>
              </button>
            </div>
          )}
          {(title || subTitle) && (
            <div className="video-item flex flex-col gap-7.5 xl:order-first xl:col-span-6 xl:col-start-2">
              {title && (
                <p
                  className="h3"
                  data-epi-edit={meta?.editHints?.title}
                  data-testid={`${dataTestId}-Title`}
                >
                  {title}
                </p>
              )}
              {subTitle && (
                <p
                  className="body-two text-grey-600 dark:text-grey-500"
                  data-epi-edit={meta?.editHints?.subTitle}
                  data-testid={`${dataTestId}-Subtitle`}
                >
                  {subTitle}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      <VideoModal
        videoTitle={title}
        videoProvider={videoProvider}
        videoId={videoId}
        isOpen={isVideoModalOpen}
        onOpenChange={setIsVideoModalOpen}
        onCloseCallback={onVideoModalClose}
        shouldCloseOnClickOutsideContent={false}
        transparent={false}
        dataTestId={`${dataTestId}-Video-Modal`}
      />
    </section>
  );
};

export default EmbeddedVideo;
